import type { McpDictionary, McpShowcaseDictionary } from "./types";

const tw = {
  metadata: {
    titleDefault: "快客數據 - 數據整合工具",
    titleTemplate: "%s | 快客數據",
    description:
      "快客數據 提供一站式的數據整合解決方案，讓您的團隊能夠快速連接、分析並視覺化來自各種來源的數據。",
    keywords: ["數據整合", "Data Studio", "數據分析", "廣告分析"],
    authorName: "快客數據",
    ogTitle: "快客數據 | 數據串接工具",
    ogDescription:
      "提供 META 廣告、GA4、GADS 串接等台灣常見資料源串接。一鍵打造數據報表，效率提升 200% 。",
    ogSiteName: "快客數據",
  },
  navbar: {
    logoText: "快客數據",
    plans: "方案介紹",
    templates: "數據模板",
    tutorials: "串接教學",
    connectors: "支援連結器",
    connectorMetaPage: "Meta 粉專",
    about: "關於快客",
    articles: "快客文章",
    signUp: "免費註冊",
  },
  footer: {
    sectionProduct: "產品",
    linkPlans: "方案介紹",
    linkTutorial: "串接教學",
    sectionCompany: "關於快客",
    linkAbout: "關於快客",
    linkArticles: "快客文章",
    linkContact: "聯絡我們",
    sectionSupport: "用戶支援",
    linkRefund: "退款政策",
    linkTerms: "服務條款",
    linkPrivacy: "隱私政策",
    logoText: "快客數據",
    tagline: "快客提供一站式的數據整合解決方案。",
    facebookPage: "粉絲專頁",
    taxId: "統一編號：96752719",
    copyright: "快客數據. All rights reserved.",
  },
  hero: {
    headline: "全媒體",
    headlineAccent: "革命報表",
    subHeadlines: ["多管道數據報表整合", 800, "AI MCP 數據閉環", 800, "3 分鐘報表生成", 800],
    ctaSignUp: "立即試用",
    platformsLabel: "支援串接平台",
    tabAll: "全部",
    dashOverview: "總覽",
    dashRange: "本月",
    kpiImpressions: "總曝光數",
    kpiClicks: "總點擊數",
    kpiConversions: "總轉換數",
    kpiConvValue: "總轉換價值",
    trendTitle: "趨勢比較",
    seriesImpressions: "曝光數",
    seriesClicks: "點擊數",
    pieTitle: "媒體佔比",
    tableTitle: "媒體成效總覽",
    tableCols: ["媒體", "曝光數", "點擊數", "轉換數", "轉換率", "轉換價值"],
    chip: "更多+",
    otherLabel: "其他",
  },
  pricing: {
    freeTrial: "免費試用",
    days: "天",
    mostPopular: "最受歡迎",
    aiMcpBadgeLabel: "MCP",
    perYear: "x 12 個月",
    perMonth: "/ 月",
    billingMonthly: "月付",
    billingYearly: "年付",
    yearlyDiscountBadge: "省 10%",
    needHelp: "需要客製服務？",
    contactLine: "聯繫 LINE 客服",
    contactMessenger: "聯絡 FB 客服",
  },
  pricingPlans: [
    {
      currency: "NT$",
      name: "基礎方案",
      priceYearly: 680,
      description: "適合大多品牌方、個人接案投手",
      isRecommended: false,
      ctaText: "立即試用",
      features: [
        { feature: "10 個綁定帳號", included: true },
        { feature: "Excel 串接", included: true },
        { feature: "Threads 海巡貼文", included: true },
        { feature: "Messenger 訊息 API", included: true },
        { feature: "KOL 搜尋 API", included: true },
        { feature: "臉書隱藏受眾", included: true },
        { feature: "自動化報告發信", included: true },
        { feature: "Google Sheet 串接", included: true },
        { feature: "Google Sheet 排程 x5", included: true },
        { feature: "Data Studio 串接", included: true },
        { feature: "Data Studio 數據模板", included: true },
        { feature: "GA4 健檢工具", included: true },
        { feature: "GTM 埋設工具", included: false },
        { feature: "Google Ads 串接", included: false },
        { feature: "Microsoft Ads 串接", included: false },
        { feature: "CYBERBIZ 串接", included: false },
        { feature: "1 SHOP 串接", included: false },
        { feature: "獨家數據儀表板", included: false },
        { feature: "額外使用者", included: false },
        { feature: "巨量模式", included: false },
      ],
    },
    {
      currency: "NT$",
      name: "進階方案",
      priceYearly: 1390,
      description: "適合想整合數據的品牌方、小型代理商",
      isRecommended: false,
      showAiMcpBadge: true,
      ctaText: "開始使用",
      features: [
        { feature: "25 個綁定帳號", included: true },
        { feature: "Excel 串接", included: true },
        { feature: "Threads 海巡貼文", included: true },
        { feature: "Messenger 訊息 API", included: true },
        { feature: "KOL 搜尋 API", included: true },
        { feature: "臉書隱藏受眾", included: true },
        { feature: "自動化報告發信", included: true },
        { feature: "Google Sheet 串接", included: true },
        { feature: "Google Sheet 排程 x15", included: true },
        { feature: "Data Studio 串接", included: true },
        { feature: "Data Studio 數據模板", included: true },
        { feature: "GA4 健檢工具", included: true },
        { feature: "GTM 埋設工具", included: true },
        { feature: "Google Ads 串接", included: true },
        { feature: "獨家數據儀表板", included: true },
        { feature: "額外使用者", included: true },
        { feature: "MCP 報表讀取", included: true },
        { feature: "MCP 廣告操作", included: false },
        { feature: "CYBERBIZ 串接", included: false },
        { feature: "1 SHOP 串接", included: false },
        { feature: "Microsoft Ads 串接", included: false },
        { feature: "巨量模式", included: false },
      ],
    },
    {
      currency: "NT$",
      name: "頂級方案",
      priceYearly: 3940,
      description: "適合大型代理商、大企業客戶",
      isRecommended: true,
      showAiMcpBadge: true,
      ctaText: "立即試用",
      features: [
        { feature: "100 個綁定帳號", included: true },
        { feature: "Excel 串接", included: true },
        { feature: "Threads 海巡貼文", included: true },
        { feature: "Messenger 訊息 API", included: true },
        { feature: "KOL 搜尋 API", included: true },
        { feature: "臉書隱藏受眾", included: true },
        { feature: "自動化報告發信", included: true },
        { feature: "Google Sheet 串接", included: true },
        { feature: "Google Sheet 排程 x50", included: true },
        { feature: "Data Studio 串接", included: true },
        { feature: "Data Studio 數據模板", included: true },
        { feature: "GA4 健檢工具", included: true },
        { feature: "GTM 埋設工具", included: true },
        { feature: "Google Ads 串接", included: true },
        { feature: "CYBERBIZ 串接", included: true },
        { feature: "1 SHOP 串接", included: true },
        { feature: "獨家數據儀表板", included: true },
        { feature: "額外使用者", included: true },
        { feature: "MCP 報表讀取", included: true },
        { feature: "MCP 廣告操作", included: true },
        { feature: "Microsoft Ads 串接", included: true },
        { feature: "巨量模式", included: true },
      ],
    },
  ],
  process: {
    heading: "每月節省",
    headingUnit: "小時",
    unitSingular: "小時",
    step1Title: "建立資料源",
    step2Title: "客製欄位",
    step3Title: "報表設計",
    step4Title: "每月報表",
  },
  featureCards: {
    heading: "數據整合",
    headingUnit: "分鐘",
    metaTitle: "Meta 產品",
    metaDesc: "Meta 廣告/粉專、IG/Threads 社群",
    googleTitle: "Google 產品",
    lineTitle: "LINE LAP",
    ecommerceTitle: "開店平台",
    ecommerceDesc: "CYBERBIZ、SHOPIFY、1SHOP、露天等電商平台",
  },
  photoCarousel: {
    heading: "用戶",
    dimensions_total: "總用戶",
    dimensions_brand: "品牌方",
    dimensions_ads: "代理商",
    graphTitle: "使用者",
  },
  featureShowcase: {
    heading: "行銷場景",
    subheading: "行銷人最齊全的工具箱",
    headingUnit: "種",
    cards: [
      {
        title: "Data Studio 報表",
        description: "三分鐘串接 META、GA4、Google Ads、LINE 等常見資料源。",
      },
      {
        title: "Sheets 串接",
        description: "將各平台原始數據同步至 Google Sheets，告別手動匯出。",
      },
      {
        title: "MCP 分析",
        description: "結合 AI 提供數據分析、優化建議，更快找到提升 ROAS 關鍵。",
      },
      {
        title: "數據監測系統",
        description: "透過 LINE 隨時監測回報廣告數據。",
      },
      {
        title: "競品素材下載",
        description: "抓取廣告檔案庫競品素材與文案。",
      },
      {
        title: "Threads 海巡",
        description: "搜尋 Threads 相關主題貼文，分析熱門貼文。",
      },
      {
        title: "Instagram 下載",
        description: "下載 Instagram 圖片與影片。",
      },
      {
        title: "BigQuery 倉儲",
        description: "將數據儲存於 BigQuery，方便進行高效分析與查詢。",
      },
      {
        title: "META 受眾查詢",
        description: "查詢 META 廣告受眾數據，進行精準投放與分析。",
      },
      {
        title: "META 操作輸出",
        description: "輸出 META 廣告操作紀錄，透過 AI 交叉分析。",
      },
    ],
  },
  mcpShowcase: {
    eyebrow: "快客 MCP",
    headingBefore: "",
    headingAccent: "1",
    headingAfter: "句話分析廣告",
    subheading:
      "在 Claude、ChatGPT、Gemini 直接用中文提問，快客 MCP 即時撈出廣告數據、畫成圖表，還能幫你調整廣告。",
    windowTitle: "快客 MCP",
    inputPlaceholder: "問問你的廣告數據…",
    disclaimer: "*對話與數據皆為模擬範例",
    scenarios: [
      {
        title: "成效查詢",
        description: "問 AI 哪個廣告活動 ROAS 最好，AI 撈出數據並提供答案。",
        steps: [
          { kind: "user", text: "上週 Meta 哪個廣告活動 ROAS 最好？" },
          {
            kind: "tool",
            label: "查詢 Meta 廣告數據",
            detail: "廣告活動層級 · 上週",
          },
          {
            kind: "text",
            text: "上週 ROAS 最高的是「雙11預熱_轉換」，達 **4.8**，花費 NT$32,400 帶回 NT$155,520 營收。",
          },
          {
            kind: "chart",
            chart: {
              kind: "ranking",
              title: "上週各廣告活動 ROAS",
              rows: [
                { label: "雙11預熱_轉換", value: 4.8, display: "4.8" },
                { label: "新品上市_再行銷", value: 3.6, display: "3.6" },
                { label: "秋季新品_廣泛受眾", value: 2.3, display: "2.3" },
                { label: "品牌形象_觸及", value: 0.9, display: "0.9" },
              ],
            },
          },
          {
            kind: "text",
            text: "「品牌形象_觸及」ROAS 只有 **0.9**，但它屬於觸及型活動，建議改用 CPM 與觸及人數來評估。",
          },
        ],
      },
      {
        title: "跨平台比較",
        description: "Meta 與 Google Ads 多管道分析應用。",
        steps: [
          {
            kind: "user",
            text: "這個月 Meta 跟 Google Ads 的花費和轉換幫我比一比",
          },
          {
            kind: "tool",
            label: "查詢 Meta 廣告數據",
            detail: "帳號層級 · 本月",
          },
          {
            kind: "tool",
            label: "查詢 Google Ads 數據",
            detail: "帳號層級 · 本月",
          },
          {
            kind: "text",
            text: "本月兩個平台合計花費 **NT$186,000**，對照如下：",
          },
          {
            kind: "chart",
            chart: {
              kind: "compare",
              title: "本月 Meta vs Google Ads",
              series: ["Meta", "Google Ads"],
              rows: [
                {
                  label: "花費",
                  values: [112000, 74000],
                  display: ["NT$112,000", "NT$74,000"],
                },
                { label: "轉換數", values: [640, 520], display: ["640", "520"] },
                {
                  label: "CPA（越低越好）",
                  values: [175, 142],
                  display: ["NT$175", "NT$142"],
                },
                { label: "ROAS", values: [3.2, 4.1], display: ["3.2", "4.1"] },
              ],
            },
          },
          {
            kind: "text",
            text: "Google Ads 的 CPA 比 Meta 低約 **19%**，ROAS 也較高，可以考慮把部分預算移往 Google 搜尋廣告。",
          },
        ],
      },
      {
        title: "廣告操作",
        description: "讓AI幫你操作廣告。一句話修改廣告後台。",
        steps: [
          { kind: "user", text: "近 7 天 ROAS 低於 1 的廣告組合幫我暫停" },
          {
            kind: "tool",
            label: "查詢 Meta 廣告數據",
            detail: "廣告組合層級 · 近 7 天",
          },
          {
            kind: "text",
            text: "找到 **3** 個 ROAS 低於 1 的廣告組合，確認後我就幫你暫停：",
          },
          {
            kind: "chart",
            chart: {
              kind: "status",
              title: "ROAS 低於 1 的廣告組合",
              columns: ["廣告組合", "ROAS", "近 7 天花費", "狀態"],
              activeLabel: "投放中",
              pausedLabel: "已暫停",
              rows: [
                { label: "秋季新品_興趣受眾A", roas: "0.62", spend: "NT$8,400" },
                { label: "秋季新品_類似受眾", roas: "0.78", spend: "NT$6,150" },
                { label: "品牌形象_再行銷", roas: "0.91", spend: "NT$4,900" },
              ],
            },
          },
          { kind: "user", text: "確認，暫停" },
          {
            kind: "tool",
            label: "調整廣告狀態",
            detail: "3 個廣告組合 → 暫停",
            action: "pause",
          },
          {
            kind: "text",
            text: "已暫停 3 個廣告組合，每週約可省下 **NT$19,450** 的低效花費。",
          },
        ],
      },
    ],
  } satisfies McpShowcaseDictionary,
  video: {
    heading: "了解如何串接",
    subHeading: "3 分鐘開始整合數據",
    defaultTitle: "串接 Data Studio 教學",
  },
  plansPage: {
    metadataTitle: "方案介紹",
    heroTitleLine1: "選擇適合的",
    heroTitleLine2: "數據整合方案",
    heroSubtitle: "無論個人用戶還是大型企業，我們都有適合您的方案。",
    additionalNotesTitle: "其他說明",
    additionalNotes: [
      "皆提供 14 天免費串接 Data Studio，無需綁定信用卡。",
      "如有大量採購企業，歡迎洽詢 LINE。",
      "串接 1 個臉書廣告帳號 + 1 個 Threads 社群帳號 = 串接 2 個帳號。",
      "採用 頂尖 Claude AI 模型進行數據分析。",
      "巨量模式適合 META 廣告月投放額 30 萬以上 / 廣告素材同時 50 檔的大量數據。",
    ],
    sampleReportTitle: "查看完整範例",
  },
  templatesPage: {
    metadataTitle: "Data Studio 模板",
    heroTitle: "業界頂尖數據模板",
    heroDescription: "Data Studio 模板",
    ctaLabel: "預約使用報表",
    ctaHref: "https://line.me/R/ti/p/@070xojro",
    cards: [
      {
        category: "meta",
        typeLabel: "Meta",
        title: "標準報告",
        description: "Facebook 廣告成效總覽與關鍵指標整理。",
        imageWidth: 1980,
        imageHeight: 1280,
        icon: "/icon/fb.png",
        image:
          "https://photo.quickdash.com.tw/banner/%E9%BB%91%E5%AE%A2-lading-page-14.jpg",
        href: "https://lookerstudio.google.com/u/0/reporting/25c98761-f852-4982-9ef5-73dc3ecd4e7b",
      },
      {
        category: "meta",
        typeLabel: "Meta",
        title: "現代版報告",
        description: "10+ 必看頂尖數據報表。",
        imageWidth: 1980,
        imageHeight: 1280,
        icon: "/icon/fb.png",
        image:
          "https://photo.quickdash.com.tw/banner/%E7%8F%BE%E4%BB%A3%E7%89%88%E5%A0%B1%E5%91%8A.png",
        href: "https://lookerstudio.google.com/reporting/c102f677-7313-4680-905d-dc9e40e36092",
      },
      {
        category: "meta",
        typeLabel: "Meta",
        title: "電商報表",
        description: "以電商角度出發的頂尖數據報表。",
        imageWidth: 1980,
        imageHeight: 1280,
        icon: "/icon/fb.png",
        image: "https://photo.quickdash.com.tw/banner/%E9%9B%BB%E5%95%86.png",
        href: "https://lookerstudio.google.com/reporting/c198063e-8dbe-486f-87bb-d4a557c3d408",
      },
      {
        category: "meta",
        typeLabel: "Meta",
        title: "非電商報表",
        description: "以非電商角度出發的頂尖數據報表。",
        imageWidth: 1980,
        imageHeight: 1280,
        icon: "/icon/fb.png",
        image: "https://photo.quickdash.com.tw/banner/%E9%9B%BB%E5%95%86.png",
        href: "https://lookerstudio.google.com/reporting/781fd967-4576-4c1a-9896-26c1d194bbf9",
      },
      {
        category: "seo",
        typeLabel: "SEO",
        title: "獨家 SEO 轉換報表",
        description: "完整掌握 SEO 轉換成效。",
        icon: "/icon/gsc.png",
        imageWidth: 1980,
        imageHeight: 1280,
        image: "https://photo.quickdash.com.tw/quickdash-admin/seo-report.png",
        href: "https://lookerstudio.google.com/reporting/1e65ebfe-6c12-477f-b582-c9b51a245dc5",
      },
      {
        category: "multichannel",
        typeLabel: "多管道",
        title: "獨家全媒體報表",
        description: "觀看更完整報表，請聯繫快客客服。",
        icon: "/wp-content/uploads/2025/10/network.png",
        imageWidth: 1980,
        imageHeight: 1280,
        image:
          "https://photo.quickdash.com.tw/quickdash-admin/Mutiple-report.png",
        href: "https://lookerstudio.google.com/reporting/c5e74d58-ebce-4a97-bfb8-d722c01ce8b1",
      },
      {
        category: "ga4",
        typeLabel: "GA4",
        title: "GA4 頂級報表",
        description: "觀看更完整報表，請聯繫快客客服。",
        icon: "/icon/ga4.png",
        imageWidth: 1980,
        imageHeight: 1280,
        image: "https://photo.quickdash.com.tw/quickdash-admin/GA4report.png",
        href: "https://lookerstudio.google.com/reporting/9f2f8b03-440a-4b4d-a4d0-8657e23e3703",
      },
      {
        category: "ga4",
        typeLabel: "GA4",
        title: "GA4 到達網頁",
        description: "觀看更完整報表，請聯繫快客客服。",
        icon: "/icon/ga4.png",
        imageWidth: 1980,
        imageHeight: 1280,
        image:
          "https://photo.quickdash.com.tw/quickdash-admin/ga4-landing-report.png",
        href: "https://lookerstudio.google.com/reporting/f8c293f7-c12e-4e45-ba68-861b9c7051e0",
      },
      {
        category: "ga4",
        typeLabel: "GA4",
        title: "GA4 主攻 / 輔助轉換報表",
        description: "掌握廣告素材主攻 / 輔助報表。",
        icon: "/icon/ga4.png",
        imageWidth: 1980,
        imageHeight: 1280,
        image: "https://photo.quickdash.com.tw/quickdash-admin/cv-report.png",
        href: "https://lookerstudio.google.com/reporting/5b8da08b-afc1-4ece-84ce-8abdff60c42e",
      },
    ],
  },
  mcpPage: {
    metadataTitle: "串接工具",
    hero: {
      title: "讓 AI 直接讀懂你的廣告數據",
      ctaUrl: "https://ai.spyder.com.tw/mcp-web",
      ctaText: "立即串接報表",
      youtubeId: "KWsLSVhtMqQ",
      youtubeTitle: " 快客 MCP Tutorial",
    },
    aiTools: {
      title: "支援串接的 AI 工具",
      description: "快客 MCP 可安裝在你習慣的 AI 工具中直接查詢廣告數據。",
      tools: [
        { name: "Claude", description: "Claude.ai", icon: "/icon/claude.png" },
        {
          name: "ChatGPT",
          description: "自訂連接器 Connectors",
          icon: "/icon/gpt.png",
        },
        { name: "Gemini", description: "Gemini", icon: "/icon/gemini.png" },
      ],
    },
    dataSources: {
      title: "支援的資料源",
      description: "一次串接台灣常見的廣告與電商資料源，數據自動同步至 AI。",
      sources: [
        { name: "Meta 廣告", icon: "/icon/fb.png" },
        { name: "Google Ads", icon: "/icon/gads.png" },
        { name: "GA4", icon: "/icon/ga4.png" },
        { name: "GSC", icon: "/icon/gsc.png" },
        { name: "LINE", icon: "/icon/line.png" },
        { name: "Microsoft Ads", icon: "/icon/bing.png" },
      ],
    },
    tools: {
      title: "可查詢的 MCP 工具",
      subtitle: "各平台支援的數據查詢工具，讓 AI 直接分析。",
      groups: {
        meta: {
          name: "Meta 廣告",
          items: [
            "帳號查詢",
            "成效數據",
            "自訂轉換",
            "操作紀錄",
            "結構查詢",
            "調整預算",
            "開關投放",
            "複製活動",
            "複製組合",
            "受眾查詢",
            "查詢素材加強創意",
            "關閉素材加強創意",
          ],
        },
        googleAds: {
          name: "Google Ads",
          items: ["帳號查詢", "成效數據"],
        },
        ga4: {
          name: "GA4",
          items: ["資源查詢", "分析數據", "自訂維度", "自訂指標"],
        },
        gsc: {
          name: "GSC",
          items: ["網站查詢", "搜尋成效"],
        },
        line: {
          name: "LINE",
          items: ["帳號查詢", "成效數據"],
        },
        microsoft: {
          name: "Microsoft Ads",
          items: ["帳號查詢", "成效數據"],
        },
      },
    },
  },
  tutorialPage: {
    metadataTitle: "串接教學",
    quickStart: {
      title: "快速開始",
      description: "3 分鐘導入數據至 Data Studio。",
      ctaUrl: "https://ai.spyder.com.tw/looker-web2",
      ctaText: "立即串接報表",
      youtubeId: "zXtAPyQeMoE",
      youtubeTitle: " 快客 Data Studio Tutorial",
    },
    steps: [
      {
        title: "1. 註冊快客會員",
        description: "",
        emphasis: "永久免費註冊會員",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊快客會員",
        },
        height: 400,
      },
      {
        title: "2. 連結資料源",
        description: "進入資料綁定頁面，\n綁定您要連接的資料源。",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A.jpg",
          alt: "連結資料源（資料綁定）",
        },
        height: 400,
        width: 600,
      },
      {
        title: "3. 前往 Data Studio",
        description: "將快客後台的密鑰\n貼至 Data Studio 中以進行驗證。",
        image: {
          src: "/wp-content/uploads/2025/10/%E7%99%BB%E5%85%A52.png",
          alt: "前往 Data Studio 驗證",
        },
        height: 400,
        width: 600,
      },
      {
        title: "4. 完成！開始使用",
        description: "開始整合數據、社群帳號等資料源。",
        image: {
          src: "/wp-content/uploads/2025/10/%E4%BF%AE%E6%94%B9.png",
          alt: "完成並開始使用",
        },
        height: 400,
        width: 600,
      },
    ],
    extraSections: [
      {
        title: "其他補充資訊",
        description:
          "如果您還想進一步了解，\n可以繼續往下看。\n看完上面的教學，\n您已經可以進行數據導入！",
      },
      {
        title: "綁定密鑰",
        description: "此密鑰為驗證的重要密鑰。\n勿將此密鑰提供給不信任的人士。",
        emphasis: "快客遵守 META 使用規範。\n無權修改/檢視帳號的詳細資料。",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A2.jpg",
          alt: "綁定密鑰",
        },
      },
      {
        title: "設定技巧 1",
        description: "任選綁定廣告帳號，\nData Studio 以這帳號呈現數據。",
        emphasis: "（顯示帳號與快客綁定帳號連動）",
        image: {
          src: "/wp-content/uploads/2024/12/%E5%AE%8C%E6%88%90.jpg",
          alt: "設定技巧 1",
        },
      },
      {
        title: "設定技巧 2",
        description: "勾選後，可在 Data Studio 中\n切換連結帳號。",
        image: {
          src: "/wp-content/uploads/2024/12/%E5%AE%8C%E6%88%90-2.jpg",
          alt: "設定技巧 2",
        },
      },
      {
        title: "設定技巧 3",
        description: "可切換 META 廣告數據歸因。",
        image: {
          src: "/wp-content/uploads/2024/12/%E5%AE%8C%E6%88%90-3.jpg",
          alt: "設定技巧 3",
        },
      },
      {
        title: "設定技巧 4",
        description: "可修改 META 廣告數據。",
        image: {
          src: "/wp-content/uploads/2024/12/%E5%AE%8C%E6%88%90-5-2.jpg",
          alt: "設定技巧 4",
        },
      },
    ],
  },
  gsheetTutorialPage: {
    metadataTitle: "Google Sheet 串接教學",
    quickStart: {
      title: "快速開始",
      description: "3 分鐘快速導入數據至 Google Sheet。",
      ctaUrl: "https://ai.spyder.com.tw/sheet-web2",
      ctaText: "串接廣告數據",
      youtubeId: "Quxa5rb6nus",
      youtubeTitle: " 快客 Google Sheet Tutorial",
    },
    steps: [
      {
        title: "1. 註冊快客會員",
        description: "",
        emphasis: "永久免費註冊會員",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊快客會員",
        },
        height: 400,
      },
      {
        title: "2. 連結資料源",
        description: "進入資料綁定頁面，\n綁定您要連接的資料源。",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A.jpg",
          alt: "連結資料源（資料綁定）",
        },
        height: 400,
      },
      {
        title: "3. 安裝插件",
        description: "",
        actionHref:
          "https://workspace.google.com/marketplace/app/%E5%BF%AB%E5%AE%A2_%E7%AC%AC%E4%B8%89%E6%96%B9%E4%B8%B2%E6%8E%A5%E5%B7%A5%E5%85%B7/663018989708",
        actionLabel: "點擊安裝",
        image: {
          src: "/wp-content/uploads/2025/09/1-1.png",
          alt: "安裝插件（Google Workspace Marketplace）",
        },
        height: 320,
      },
      {
        title: "4. 開啟試算表",
        description:
          "打開 Google Sheet 試算表。\n點選「擴充工具」找到快客。\n再點選執行。",
        image: {
          src: "/wp-content/uploads/2025/05/7.jpg",
          alt: "開啟試算表並執行快客",
        },
        height: 260,
      },
      {
        title: "5. 選擇功能",
        description: "選擇 META 廣告。",
        image: {
          src: "/wp-content/uploads/2025/05/5-1.png",
          alt: "選擇功能（META 廣告）",
        },
        height: 220,
      },
      {
        title: "6. 設定廣告數據",
        description: "選擇帳號、指標、日期、歸因等。\n點選產生報告，完成！",
        image: {
          src: "/wp-content/uploads/2025/05/6.png",
          alt: "設定廣告數據並產生報告",
        },
        height: 420,
      },
    ],
    extraSections: [
      {
        title: "額外功能介紹",
        description:
          "如果您還想進一步了解，\n可以繼續往下看。\n看完上面的教學，\n您已經可以進行數據導入！",
      },
      {
        title: "綁定密鑰",
        description: "此密鑰為驗證的重要密鑰。\n勿將此密鑰提供給不信任的人士。",
        emphasis: "快客遵守 META 使用規範。\n無權修改/檢視帳號的詳細資料。",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A2.jpg",
          alt: "綁定密鑰",
        },
      },
      {
        title: "儲存報告",
        description: "透過儲存報告，快速調閱過往設定的帳號、指標。",
        image: {
          src: "/wp-content/uploads/2025/06/%E5%84%B2%E5%AD%98%E5%A0%B1%E5%91%8A.png",
          alt: "儲存報告",
        },
        height: 420,
        width: 280,
      },
      {
        title: "自動化排程",
        description: "支援自動化更新，更新方式可選擇「覆蓋、附加」兩種模式。",
        image: {
          src: "/wp-content/uploads/2025/06/%E6%8E%92%E7%A8%8B.png",
          alt: "自動化排程",
        },
        height: 340,
        width: 280,
      },
      {
        title: "自動化發信",
        description: "將 Google Sheet 中的報告轉 PDF 檔案寄送至指定信箱。",
        emphasis: "",
        image: {
          src: "/wp-content/uploads/2025/06/%E7%99%BC%E4%BF%A1.png",
          alt: "自動化發信",
        },
        height: 420,
        width: 280,
      },
    ],
  },
  extensionTutorialPage: {
    metadataTitle: "Chrome 擴充插件教學",
    quickStart: {
      title: "快速開始",
      description: "開始抓取廣告素材，快速完成設定。",
      ctaUrl: "https://ai.spyder.com.tw/yt-web9/web",
      ctaText: "抓取廣告素材",
      youtubeId: "Gj3jdnO3Uco",
      youtubeTitle: " 快客 Chrome Extension Tutorial",
    },
    toolsSection: {
      title: "擴充多種功能",
      description: "安裝完成後，即可在瀏覽器中使用以下功能。",
      tools: [
        {
          name: "廣告數據報表",
          description: "一鍵查詢 Meta 報表。",
        },
        {
          name: "隱藏受眾查詢",
          description: "查詢 Meta 後台的興趣受眾關鍵字。",
        },
        {
          name: "SEO 網站爬蟲",
          description: "爬取網站標題、描述與標籤結構，快速健檢 SEO。",
        },
        {
          name: "GA4 設定檢測",
          description: "檢測 GA4 是否正確安裝，並檢視資源設定。",
        },
        {
          name: "GTM 事件安裝",
          description: "快速部署 GTM 代碼與事件追蹤。",
        },
        {
          name: "電商平台查詢",
          description: "辨識網站所使用的電商平台與前端技術架構。",
        },
        {
          name: "Meta 檔案庫輸出",
          description: "Meta 廣告檔案庫圖片與文案。",
        },
        {
          name: "IG 圖影下載",
          description: "下載 Instagram 貼文的圖片與影片內容。",
        },
        {
          name: "廣告素材輸出",
          description: "匯出廣告素材。",
        },
        {
          name: "廣告紀錄輸出",
          description: "匯出廣告帳號的變更操作紀錄。",
        },
      ],
    },
    steps: [
      {
        title: "1. 註冊快客會員",
        description: "",
        emphasis: "永久免費串接",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊快客會員",
        },
        height: 400,
      },
      {
        title: "2. 連結資料源",
        description: "進入資料綁定頁面，\n綁定您要連接的資料源。",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A.jpg",
          alt: "連結資料源（資料綁定）",
        },
        height: 400,
      },
      {
        title: "3. 安裝插件",
        description: "前往 Chrome 線上應用程式商店安裝快客擴充功能。",
        actionHref:
          "https://chromewebstore.google.com/detail/dagdchpdhalojgcfnhbnmkcbjoggbclb",
        actionLabel: "點擊安裝",
      },
      {
        title: "4. 開啟擴充插件",
        description: "瀏覽器右上角，找到快客 LOGO Icon。",
        image: {
          src: "/icon/logo.png",
          alt: "開啟擴充插件",
        },
        height: 100,
      },
      {
        title: "5. 點選登入",
        description: "左側選單點選「快客登入」。",
        image: {
          src: "/wp-content/uploads/2025/05/c3.png",
          alt: "快客登入",
        },
        height: 420,
      },
      {
        title: "6. 貼上後台金鑰",
        description: "貼上在快客後台的金鑰。",
        image: {
          src: "/wp-content/uploads/2025/05/c5.png",
          alt: "貼上後台金鑰",
        },
        height: 320,
      },
      {
        title: "7. 完成",
        description: "開始數據拉取\n隱藏受眾查詢\n素材匯出等功能！",
        image: {
          src: "/wp-content/uploads/2025/05/c6.png",
          alt: "完成並開始使用",
        },
        height: 420,
      },
    ],
    extraSections: [
      {
        title: "其他補充資訊",
        description:
          "如果您還想進一步了解，\n可以繼續往下看。\n看完上面的教學，\n您已經可以進行數據導入！",
      },
      {
        title: "綁定密鑰",
        description: "此密鑰為驗證的重要密鑰。\n勿將此密鑰提供給不信任的人士。",
        emphasis: "快客遵守 META 使用規範。\n無權修改/檢視帳號的詳細資料。",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A2.jpg",
          alt: "綁定密鑰",
        },
      },
    ],
  },
  metaAdsPage: {
    metadataTitle: "Meta Ads",
    heroTitle: "META Ads 串接",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio、Chrome Extension。",
    ctaLabel: "可查詢指標",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: ["廣告成效", "電商成效", "受眾成效", "廣告素材", "ROAS"],
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-2.gif",
        alt: "Chrome Extension",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI 分析",
      },
    ],
    quickdashSectionTitle: "快客 & Meta Ads 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Chrome Extension、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 META Ads",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定要串接 Meta Ads 的廣告帳號。",
        image: {
          src: "/wp-content/uploads/2025/07/5.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "依據您的終點（GSheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/08/18.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
    notesTitle: "注意事項",
    notesDescription: "如需串接 META 廣告數據，請參考下方設定示意：",
    notesImages: [
      {
        src: "/wp-content/uploads/2025/04/16.png",
        alt: "Meta Ads 權限設定示意",
        caption: "前往該廣告帳號設定",
      },
      {
        src: "/wp-content/uploads/2025/08/landing.png",
        alt: "串接示意",
        caption: "需有該廣告帳號「查看成效」權限",
      },
    ],
  },
  igInsightsPage: {
    metadataTitle: "Instagram Insights",
    heroTitle: "Instagram",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio、Chrome Extension。",
    ctaLabel: "可查詢指標",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: ["貼文圖片", "貼文連結", "帳戶成效", "短影成效"],
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    quickdashSectionTitle: "快客 & Instagram 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Chrome Extension、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 Instagram Insights",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定要串接 IG 的帳號。",
        image: {
          src: "/wp-content/uploads/2025/07/3.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "依據您的終點（GSheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/08/15.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
    notesTitle: "注意事項",
    notesDescription: "如需串接 Instagram 社群數據，需確認有完成以下設定：",
    notesChecklist: [
      "IG 需連結 FB 粉專",
      "FB 粉專綁定企業管理平台（BM）",
      "需有該粉專「洞察報告」權限",
    ],
    notesImages: [
      {
        src: "/wp-content/uploads/2025/04/9.png",
        alt: "IG 連結 FB 粉專示意",
        caption: "1. IG 需連結 FB 粉專",
      },
      {
        src: "/wp-content/uploads/2025/09/21-1.png",
        alt: "粉專綁定企業管理平台示意",
        caption: "2. FB 粉專綁定企業管理平台（BM）",
      },
      {
        src: "/wp-content/uploads/2025/04/11.png",
        alt: "洞察報告權限示意",
        caption: "3. 需有該粉專「洞察報告」權限",
      },
    ],
  },
  threadsPage: {
    metadataTitle: "Threads",
    heroTitle: "Threads",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio、Chrome Extension。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/thread-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: ["貼文圖片", "貼文連結", "帳戶成效", "觸及趨勢"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-2.gif",
        alt: "Chrome Extension",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/02/THREAD.png",
      alt: "Threads",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "快客 & Threads 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Chrome Extension、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 Threads Insights",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定要串接 Threads 的帳號。",
        image: {
          src: "/wp-content/uploads/2025/07/4.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "依據您的終點（GSheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/08/16.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
    notesTitle: "注意事項",
    notesDescription: "如需串接 Threads 社群數據，需確認有完成以下：",
    notesChecklist: [
      "電腦瀏覽器登入 Threads",
      "快客綁定 Threads",
      "一次僅能綁定一支 Threads",
    ],
    notesImages: [
      {
        src: "/wp-content/uploads/2025/05/20.png",
        alt: "Threads 登入示意",
        caption: "電腦瀏覽器登入 Threads",
      },
      {
        src: "/wp-content/uploads/2025/05/21.png",
        alt: "快客綁定 Threads 示意",
        caption: "快客綁定 Threads",
      },
    ],
    notesFrameHeight: 700,
  },
  lapPage: {
    metadataTitle: "Line Lap",
    heroTitle: "LINE Lap",
    heroTitleLine2: "廣告串接",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio。",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: ["電商成效", "App 成效", "遊戲成效"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-3",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI 分析",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/03/line-1.png",
      alt: "Line Lap",
      width: 220,
      height: 220,
    },
    quickdashSectionTitle: "Line Lap 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 Line Lap",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定「尬廣數位」的廣告帳號。",
        image: {
          src: "/wp-content/uploads/2025/07/2.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "依據您的終點（GSheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/08/11-2.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  metaPagePage: {
    metadataTitle: "Meta 粉專",
    heroTitle: "META Page",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio、Chrome Extension。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/metapage-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: ["貼文圖片", "貼文連結", "貼文文案", "貼文成效"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-2.gif",
        alt: "Chrome Extension",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/04/%E5%BF%AB%E5%AE%A2-Gsheet-%E9%A6%96%E9%A0%81icon.png",
      alt: "Meta 粉專",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "快客 & Meta 粉專串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Chrome Extension、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 META 粉專",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定要串接的 FB 粉專。",
        image: {
          src: "/wp-content/uploads/2025/07/landing-1-1.png",
          alt: "綁定粉專",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "依據您的終點（GSheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/07/14.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
    notesTitle: "注意事項",
    notesDescription: "如需串接 FB 粉專數據，需確認有完成以下：",
    notesChecklist: [
      "FB 粉專需綁定企業管理平台（BM）",
      "需有該粉專「洞察報告」權限",
    ],
    notesImages: [
      {
        src: "/wp-content/uploads/2025/09/20.png",
        alt: "粉專綁定企業管理平台示意",
        caption: "FB 粉專需綁定企業管理平台（BM）",
      },
      {
        src: "/wp-content/uploads/2025/04/14.png",
        alt: "洞察報告權限示意",
        caption: "需有該粉專「洞察報告」權限",
      },
    ],
    notesFrameHeight: 700,
  },
  gaPage: {
    metadataTitle: "GA4",
    heroTitle: "Google Analytics 4",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/ga-options/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: [
      "事件報告",
      "到達網頁",
      "流量開發",
      "獲取新客",
      "網頁畫面",
      "電子商務",
      "電商漏斗",
      "人口統計",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI 分析",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/07/ga-2.png",
      alt: "Google Analytics 4",
      width: 260,
      height: 260,
      imageClassName: "w-44 h-44 sm:w-56 sm:h-56 object-contain",
    },
    quickdashSectionTitle: "快客 & GA4 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 GA4",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定要串接的 GA4 帳號。",
        image: {
          src: "/wp-content/uploads/2025/07/landing-1.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "依據您的終點（Google Sheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/11/14.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  gadsPage: {
    metadataTitle: "Google Ads",
    heroTitle: "Google Ads",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/gads-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: [
      "廣告活動報告",
      "廣告群組報告",
      "動態搜尋報告",
      "出價策略報告",
      "帳戶報告",
      "點擊報告",
      "互動報告",
      "影片報告",
      "關鍵字報告",
      "搜尋報告",
      "裝置報告",
      "年齡報告",
      "性別報告",
      "地理報告",
      "曝光比重報告",
      "GAds + GA4 報告",
      "轉換報告",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI 分析",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/06/%E6%9C%AA%E5%91%BD%E5%90%8D%E8%A8%AD%E8%A8%88.png",
      alt: "Google Ads",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "快客 & GAds 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 Google Ads",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定要串接的 Google Ads 帳號。",
        image: {
          src: "/wp-content/uploads/2025/08/9-2.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "於 Google Sheet、Data Studio 貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/08/13-1.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  bingPage: {
    metadataTitle: "Microsoft Ads",
    heroTitle: "Microsoft Ads",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/microsoft-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: [
      "帳戶報告",
      "廣告活動報告",
      "廣告群組報告",
      "廣告報告",
      "關鍵字報告",
      "搜尋字詞報告",
      "裝置報告",
      "年齡性別報告",
      "地理報告",
      "曝光比重報告",
      "資產群組報告（PMax）",
      "轉換報告",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI 分析",
      },
    ],
    heroImage: {
      src: "/icon/bing.png",
      alt: "Microsoft Ads",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "快客 & Microsoft Ads 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 Microsoft Ads",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定要串接的 Microsoft Ads 帳號。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "於 Google Sheet、Data Studio 貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  gbpPage: {
    metadataTitle: "Google 我的商家",
    heroTitle: "Google 我的商家",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio、MCP。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/gbp-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: [
      "門市識別",
      "時間",
      "商家成效",
      "搜尋關鍵字",
      "評論明細",
      "評論統計",
      "目前評分",
      "商家資訊",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI 分析",
      },
    ],
    heroImage: {
      src: "/icon/GBP.png",
      alt: "Google Business Profile",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "快客 & Google 商家串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 Google 商家",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定要串接的 Google 商家帳號。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "於 Google Sheet、Data Studio 貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  openaiPage: {
    metadataTitle: "OpenAI Ads",
    heroTitle: "OpenAI Ads",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio、MCP。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/oai-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: [
      "帳戶報告",
      "廣告活動報告",
      "廣告群組報告",
      "廣告報告",
      "素材報告",
      "每日成效報告",
      "國家報告",
      "裝置報告",
      "平台報告",
      "商品報告",
      "轉換事件報告",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI 分析",
      },
    ],
    heroImage: {
      src: "/icon/openai.png",
      alt: "OpenAI Ads",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "快客 & OpenAI Ads 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 OpenAI Ads",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定要串接的 OpenAI Ads 帳號。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "於 Google Sheet、Data Studio 貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  gscPage: {
    metadataTitle: "GSC",
    heroTitle: "Search Console",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/gsc-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: ["網站排名", "網站點擊", "網站點擊率", "網站曝光"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/09/%E6%9C%AA%E5%91%BD%E5%90%8D%E8%A8%AD%E8%A8%88.png",
      alt: "Search Console",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "快客 & GSC 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 GSC",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定要串接的 GSC 網站。",
        image: {
          src: "/wp-content/uploads/2025/07/7.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "依據您的終點（GSheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/08/12.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  cyberbizPage: {
    metadataTitle: "CYBERBIZ",
    heroTitle: "CYBERBIZ",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/cyberbiz-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: ["會員資料", "訂單資料"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/11/568981694_1134786405497881_6970468732146188645_n.jpg",
      alt: "CYBERBIZ",
      width: 320,
      height: 320,
      imageClassName: "rounded-2xl w-48 h-48 sm:w-64 sm:h-64 object-contain",
    },
    quickdashSectionTitle: "快客 & CYBERBIZ 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 CYBERBIZ",
    steps: [
      {
        title: "1. 註冊會員",
        description: "註冊一個您的專屬帳號，快客提供永久免費試用。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description:
          "綁定要串接的 CYBERBIZ 帳號。綁定後 CYBERBIZ APP Market 會自動建立快客應用程式。",
        image: {
          src: "/wp-content/uploads/2025/11/9.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "依據您的終點（Google Sheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/11/landing.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  shopifyPage: {
    metadataTitle: "SHOPIFY",
    heroTitle: "SHOPIFY",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/shopify-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: ["會員資料", "訂單資料", "訂單歸因", "產品資料"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/11/1865631fe5aad6ba138c6d83dc42aa70.png",
      alt: "SHOPIFY",
      width: 320,
      height: 320,
      wrapperClassName:
        "rounded-2xl border border-white/40 flex items-center justify-center",
      imageClassName: "w-48 h-48 sm:w-64 sm:h-64 object-contain",
    },
    quickdashSectionTitle: "快客 & SHOPIFY 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 SHOPIFY",
    steps: [
      {
        title: "1. 註冊會員",
        description:
          "註冊一個您的專屬帳號，快客提供永久免費試用。此串接需頂級方案。",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description:
          "綁定要串接的 SHOPIFY 帳號。綁定後 SHOPIFY APP Market 會自動創造快客應用程式。",
        image: {
          src: "/wp-content/uploads/2025/12/landing.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "依據您的終點（Google Sheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/wp-content/uploads/2025/12/landing-1.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  shoplinePage: {
    metadataTitle: "SHOPLINE",
    heroTitle: "SHOPLINE",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/shopline-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: ["會員資料", "訂單資料", "產品資料", "推廣資料"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/icon/shopline.jpg",
      alt: "SHOPLINE",
      width: 320,
      height: 320,
      wrapperClassName:
        "rounded-2xl border border-white/40 flex items-center justify-center",
      imageClassName: "w-48 h-48 sm:w-64 sm:h-64 rounded-xl object-contain",
    },
    quickdashSectionTitle: "快客 & SHOPLINE 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "4 個步驟串接 SHOPLINE",
    steps: [
      {
        title: "1. 註冊會員",
        description:
          "註冊一個您的專屬帳號，快客提供永久免費試用。此串接需要頂級方案。",
        image: {
          src: "/photos/login.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 綁定帳號",
        description: "綁定要串接的 SHOPLINE 帳號。",
        image: {
          src: "/photos/shopline.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 貼上密鑰",
        description: "依據您的終點（Google Sheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/photos/SLReport.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  oneshopPage: {
    metadataTitle: "1SHOP",
    heroTitle: "1SHOP",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/1shop-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: ["訂單資料"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/icon/one_shop.png",
      alt: "1SHOP",
      width: 320,
      height: 320,
      wrapperClassName:
        "rounded-2xl border border-white/40 flex items-center justify-center",
      imageClassName: "w-48 h-48 sm:w-64 sm:h-64 rounded-xl object-contain",
    },
    quickdashSectionTitle: "快客 & 1SHOP 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "5 個步驟串接 1SHOP",
    steps: [
      {
        title: "1. 申請金鑰",
        description:
          "加入 1SHOP 官方 LINE 申請 API 金鑰。串接金鑰需要 1SHOP 無限方案。",
        image: {
          src: "/photos/add_1shop.png",
          alt: "申請金鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 註冊會員",
        description:
          "註冊一個您的專屬帳號，快客提供永久免費試用。此串接需要頂級方案。",
        image: {
          src: "/photos/login.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 綁定帳號",
        description: "將步驟一拿到的 APP ID, APP SECRET 貼上。",
        image: {
          src: "/photos/1shop_binding.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 貼上密鑰",
        description: "依據您的終點（Google Sheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "5. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/photos/1shop_report.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  rutenPage: {
    metadataTitle: "露天市集",
    heroTitle: "露天市集",
    heroDescriptionLine1: "自動將數據傳輸至第三方平台。",
    heroDescriptionLine2: "支援 Google Sheet、Data Studio。",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/ruten-metrics/",
    supportedQueriesTitle: "支援以下查詢",
    supportedQueries: ["訂單資料"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/icon/ruten.png",
      alt: "露天市集",
      width: 320,
      height: 320,
      wrapperClassName:
        "rounded-2xl border border-white/40 flex items-center justify-center",
      imageClassName: "w-48 h-48 sm:w-64 sm:h-64 rounded-xl object-contain",
    },
    quickdashSectionTitle: "快客 & 露天市集 串接",
    quickdashSectionDescription: "高效報表製作幫手，協助打造多樣的數據儀表板。",
    featureCards: [
      {
        title: "全自動報表",
        description: "全自動依照時間更新報表。",
      },
      {
        title: "快速整頓資料",
        description: "將資料轉成易讀的數據報表。",
      },
      {
        title: "多樣管道",
        description: "支援 Google Sheet、Data Studio。",
      },
    ],
    stepsTitle: "5 個步驟串接 露天市集",
    steps: [
      {
        title: "1. 申請金鑰",
        description: "填寫露天市集申請 API 表單（私訊索取）。",
        image: {
          src: "/photos/add_ruten.png",
          alt: "申請金鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. 註冊會員",
        description:
          "註冊一個您的專屬帳號，快客提供永久免費試用。此串接需要頂級方案。",
        image: {
          src: "/photos/login.png",
          alt: "註冊會員",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. 綁定帳號",
        description: "將步驟一拿到的對應密鑰貼上。",
        image: {
          src: "/photos/ruten_binding.png",
          alt: "綁定帳號",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. 貼上密鑰",
        description: "依據您的終點（Google Sheet / Data Studio）貼上密鑰。",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "貼上密鑰",
          width: 760,
          height: 480,
        },
      },
      {
        title: "5. 完成匯入",
        description: "完成後即可開始匯入並製作報表。",
        image: {
          src: "/photos/ruten-report.png",
          alt: "完成匯入",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  aboutPage: {
    metadataTitle: "關於快客",
    heroTitleLine1: "行銷/投手",
    heroTitleHighlight: "必備數據整合工具",
    heroDescription:
      "協助行銷人、投手掌握廣告成效，生成視覺化報表，專注讓數據驅動決策。",
    stats: [
      { value: "2000+", label: "串接用戶" },
      { value: "14 小時+", label: "省下時間" },
      { value: "10+", label: "支援平台" },
      { value: "200%", label: "工作效率" },
    ],
    featuresTitle: "為何使用快客？",
    featuresSubtitle: "讓行銷人、廣告投手每月節省 14+工作小時。",
    productFeatures: [
      {
        icon: "globe",
        title: "中文化指標",
        description: "完整中文化指標與說明文件，讓您無需翻譯即可快速上手",
      },
      {
        icon: "zap",
        title: "快速串接",
        description: "3 分鐘內完成各大平台數據串接，省時省力",
      },
      {
        icon: "barChart3",
        title: "視覺化報告",
        description: "產出淺顯易懂的視覺化報告，數據一目瞭然",
      },
      {
        icon: "users",
        title: "台灣團隊支援",
        description: "由台灣技術團隊全力支援，溝通無障礙",
      },
    ],
    platformsTitle: "支援平台串接",
    platformsSubtitle: "完整支援 META、Google、LINE 等主流行銷平台",
    platforms: [
      { name: "META 廣告", iconSrc: "/icon/fb.png" },
      { name: "Instagram", iconSrc: "/icon/ig.png" },
      { name: "Threads", iconSrc: "/icon/threads.png" },
      { name: "META 粉絲專頁", iconSrc: "/icon/fb.png" },
      { name: "Google Analytics 4", iconSrc: "/icon/ga4.png" },
      { name: "Google Ads", iconSrc: "/icon/gads.png" },
      { name: "Google Search Console", iconSrc: "/icon/gsc.png" },
      { name: "LINE LAP", iconSrc: "/icon/line.png" },
      { name: "CYBERBIZ 商店", iconSrc: "/icon/cyberbiz.png" },
      { name: "SHOPIFY 商店", iconSrc: "/icon/shopify.png" },
      // { name: "SHOPLINE 商店", iconSrc: "/icon/shopline.jpg" },
      { name: "1SHOP 商店", iconSrc: "/icon/one_shop.png" },
      { name: "露天市集", iconSrc: "/icon/ruten.png" },
      { name: "Microsoft Ads", iconSrc: "/icon/bing.png" },
      { name: "lihi 短網址", iconSrc: "/icon/lihi2.png" },
    ],
    ctoBadge: "黑客數位 - 阿泰",
    ctoTitle: "領導團隊製作",
    ctoParagraphs: [
      "完成台灣首位開發創舉，包括 META 廣告、社群串接、Google Ads 串接等重要平台數據。團隊深耕 GA4、Facebook 廣告、Google Ads 等主流行銷工具，擁有橫跨台灣各大數據平台的整合與應用經驗。",
    ],
    ctoSocialLinks: [
      {
        label: "Facebook",
        href: "https://www.facebook.com/theodorekoung/",
        iconSrc: "/icon/fb.png",
      },
      {
        label: "Instagram",
        href: "https://www.instagram.com/atai_coding_data",
        iconSrc: "/icon/ig.png",
      },
      {
        label: "Threads",
        href: "https://www.threads.com/@atai_coding_data",
        iconSrc: "/icon/threads.png",
      },
    ],
    awardsSection: {
      sectionLabel: "殊榮獎項",
      items: {
        growth: {
          title: "產品專家金級",
          subtitle: "Google Analytics 4",
          description: "Google GA4 產品專家。已經累積超過 100+ 個社群解答。",
        },
        strategy: {
          title: "認證開發者",
          subtitle: "Data Studio",
          description:
            "Google Data Studio 連結器開發者，協助用戶串接數據至 Data Studio。",
        },
      },
      highlight: {
        title: "產品專家金級",
        subtitle: "Google Analytics 4",
        description:
          "黑客數位為 Data Studio 連結器開發者，以及全球 GA4 產品金級專家。",
      },
      ariaPrevious: "查看上一個獎項",
      ariaNext: "查看下一個獎項",
      ariaSwitchTo: "切換到",
    },
    testimonialsTitle: "快客推薦用戶",
    testimonials: [
      {
        name: "小圭老師",
        company: "圭話行銷創辦人",
        avatar: "/people/TURTLE.jpg",
        role: "代理商",
      },
      {
        name: "Dennis",
        company: "數位簡報室創辦人",
        avatar: "/people/DENNIS.jpg",
        role: "數據分析師",
      },
      {
        name: "Bibi",
        company: "宇動數位創辦人",
        avatar: "/people/BIBI.jpg",
        role: "代理商",
      },
      {
        name: "LEO",
        company: "國泰金控數位規劃部",
        avatar: "/people/LEO.jpg",
        role: "品牌方",
      },
      {
        name: "貝克菜",
        company: "lihi 行銷工具執行長",
        avatar: "/people/BK.png",
        role: "品牌方",
      },
      {
        name: "Hugo Chen",
        company: "先拜顧問創辦人",
        avatar: "/people/HUGO.jpg",
        role: "代理商",
      },
      {
        name: "Daisy",
        company: "品牌行銷顧問",
        avatar: "/people/Daisy.jpg",
        role: "代理商",
      },
    ],
    hackerSectionCtaText: "前往黑客數位",
    hackerSectionCtaHref: "https://spyder.com.tw/",
    hackerSectionTitle: "數位行銷數據解決團隊",
    hackerSectionParagraphs: [
      "黑客數位是一家專注於數位行銷數據解決方案的專業團隊，提供 GA4 導入、GTM 事件追蹤、BigQuery 數據分析、SEO 轉換報告等多元服務。",
      "黑客數位為官方 GA4 產品金級專家。執行超過 100 個 GA4 與 GTM 專案，協助台灣企業建立完整的數據追蹤架構，提升行銷決策效率。透過精準的資料整合與視覺化報告，致力於讓企業真正看懂數據、用好數據，實現數位轉型與效益最大化。",
    ],
    hackerSectionServices: [
      "GA4 數據分析",
      "GTM 事件追蹤",
      "BigQuery 數據分析",
      "SEO 獨家報告",
    ],
    hackerSectionStats: [
      { value: "100+", label: "GA4 專案" },
      { value: "100+", label: "GTM 專案" },
    ],
    hackerSectionAwardText: "企業數據解決方案專家",
  },
  contactPage: {
    metadataTitle: "聯絡我們",
    heroTitle: "聯絡我們",
    heroDescription:
      "如有任何快客操作問題，請聯繫快客官方 LINE，將有專人為您服務。",
    lineButtonLabel: "加入 LINE 官方帳號",
    lineOfficialUrl:
      "https://line.me/R/ti/p/@070xojro?ts=12171155&oat_content=url",
  },
  refundPage: {
    metadataTitle: "退款政策",
    heroTitle: "退款政策",
    heroDescription:
      "依照以下流程提交退款申請，客服將協助您計算退款金額並完成確認。",
    lineButtonLabel: "加入 LINE 官方帳號",
    lineOfficialUrl:
      "https://line.me/R/ti/p/@070xojro?ts=12171155&oat_content=url",
    stepsTitle: "退款流程",
    steps: [
      {
        title: "加入 LINE 官方帳號",
        description:
          "請先加入快客官方 LINE，後續將透過 LINE 進行退款申請與確認。",
      },
      {
        title: "輸入以下訊息",
        description: "依序傳送指定訊息，讓客服協助計算退款金額。",
        items: [
          "傳送「我要退款」。",
          "提供「註冊信箱」。",
          "等待計算退款金額並確認。",
        ],
      },
    ],
    notesTitle: "退款需知",
    notes: [
      "每月以 30 天為計算。",
      "退款需要支付額外 5% 手續費。",
      "退款後，所有快客服務皆會馬上取消。",
      "退款申請日以傳送至 LINE 訊息當天起算。",
      "退款計算：方案剩餘天數 × 方案平均價格，再扣 5% 退款手續費。",
      "如有串接 Data Studio 一樣有報表權限，但連接器皆會失效。",
      "Google Sheet 排程會即刻失效。",
      "快客保留最終解釋權利。",
    ],
  },
  policyPage: {
    metadataTitle: "隱私權政策",
    heroTitle: "隱私權保護政策",
    heroDescription:
      "快客數據（以下簡稱本網站），為了讓您能夠安心使用本網站的各項服務與資訊，特此向您說明本網站的隱私權保護政策，以保障您的權益，請您詳閱下列內容：",
    sections: [
      {
        title: "一、隱私權保護政策的適用範圍",
        paragraphs: [
          "隱私權保護政策內容，包括本網站如何處理在您使用網站服務時收集到的個人識別資料。隱私權保護政策不適用於本網站以外的相關連結網站，也不適用於非本網站所委託或參與管理的人員。",
        ],
      },
      {
        title: "二、個人資料的蒐集、處理及利用方式",
        paragraphs: [
          "當您造訪本網站或使用本網站所提供之功能服務時，我們將視該服務功能性質，請您提供必要的個人資料，並在該特定目的範圍內處理及利用您的個人資料；非經您書面同意，本網站不會將個人資料用於其他用途。",
          "本網站在您使用服務信箱、問卷調查等互動性功能時，會保留您所提供的姓名、電子郵件地址、聯絡方式及使用時間等。於一般瀏覽時，伺服器會自行記錄相關行徑，包括您使用連線設備的 IP 位址、使用時間、使用的瀏覽器、瀏覽及點選資料記錄等，作為我們增進網站服務的參考依據，此記錄為內部應用，絕不對外公佈。",
          "為提供精確的服務，我們會將收集的問卷調查內容進行統計與分析，分析結果之統計數據或說明文字呈現，除供內部研究外，我們會視需要公佈統計數據及說明文字，但不涉及特定個人之資料。",
        ],
      },
      {
        title: "三、資料之保護",
        paragraphs: [
          "只由經過授權的人員才能接觸您的個人資料，如因業務需要有必要委託其他單位提供服務時，本網站亦會嚴格要求其遵守保密義務，並且採取必要檢查程序以確定其將確實遵守。",
        ],
        showRightsRequests: true,
      },
      {
        title: "四、Google 用戶資料 — 存取、使用與保護",
        paragraphs: [
          "",
        ],
        subsections: [
          {
            heading: "存取的資料",
            paragraphs: [
              "在用戶透過 Google 同意流程授權後，快客會存取以下資料：",
            ],
            items: [
              "Google 相關帳戶與資源中繼資料（GA4、GSC、GAds 等）（帳戶 ID、帳戶名稱、資源 ID、資源名稱）。",
              "匯總報表資料，基於用戶選擇的指標（例如：工作階段、總使用者數、網頁瀏覽量、轉換次數、收益）與維度（例如：來源/媒介、裝置類別、國家/地區、到達網頁）。",
            ],
            notes: [
              "快客不會透過此授權範圍存取個人識別資訊（PII），例如個別用戶 ID、IP 位址或電子郵件地址。",
            ],
          },
          {
            heading: "資料的使用方式",
            paragraphs: ["Google 用戶資料僅用於以下用途："],
            items: [
              "在快客後台中顯示用戶的 GA4 帳戶與資源。",
              "根據用戶選擇的維度和指標，擷取並呈現 GA4 報表資料。",
              "允許用戶主動將 GA4 資料匯出至其選擇的目的地，例如 Google 試算表（Google Sheets）、Data Studio 或相容於 MCP 的分析平台。",
            ],
            notes: [
              "所有資料擷取與匯出操作皆需要用戶明確發起。在沒有用戶直接操作的情況下，快客不會自動存取、查詢或處理 Google 用戶資料。Google 用戶資料不會用於精準投放廣告、信用或貸款評估，或任何與上述分析功能無關的目的。",
            ],
          },
          {
            heading: "資料分享與傳輸",
            paragraphs: [
              "快客不會主動將 Google 用戶資料出售、交易或轉讓給資料仲介、廣告聯播網或任何第三方。",
              "只有在用戶透過快客匯出功能（Google 試算表、Data Studio、MCP）明確發起時，才會進行資料傳輸。由用戶自行決定何時、何地以及要匯出哪些資料。 快客 僅作為用戶授權的資料橋樑，不會代替用戶決定匯出的目的地。",
            ],
          },
          {
            heading: "資料保護",
            items: [
              "傳輸中的所有資料皆透過 HTTPS（TLS 1.2+）進行加密。",
              "用戶授權資料儲存於雲端基礎架構的加密磁碟區中。",
              "用戶資料的存取權限依循「最小權限原則」，僅限授權的系統與人員存取。",
              "保留存取日誌以供稽核與追蹤使用。",
              "定期進行安全性審查與漏洞修補。",
            ],
          },
          {
            heading: "資料保留與刪除",
            paragraphs: [
              "僅在用戶的快客帳戶處於啟用狀態且 Google 授權保持連線時，才會保留權杖。",
            ],
            items: [
              "用戶隨時可以從其快客帳戶設定中撤銷 Google 授權。一旦撤銷，所有儲存的 Google 權杖將立即被刪除。",
              "用戶可透過快客 LINE 要求刪除帳戶。",
              "用戶也可以透過其 Google 帳戶權限頁面撤銷快客的存取權限。",
            ],
          },
          {
            heading: "AI/機器學習模型訓練",
            paragraphs: [
              "快客不會使用 Google 用戶資料來開發、訓練或改善任何人工智慧（AI）或機器學習模型（無論是快客還是任何第三方擁有的模型）。",
            ],
          },
          {
            heading: "限制使用規範遵循",
            paragraphs: [
              "快客將從 Google API 接收到的資訊使用與傳輸至任何其他應用程式時，將嚴格遵守《Google API 服務用戶資料政策》（Google API Services User Data Policy），包含其「限制使用」（Limited Use）的相關規範。",
            ],
          },
        ],
      },
      {
        title: "五、網站對外的相關連結",
        paragraphs: [
          "本網站的網頁提供其他網站的網路連結，您也可經由本網站所提供的連結，點選進入其他網站。但該連結網站不適用本網站的隱私權保護政策，您必須參考該連結網站中的隱私權保護政策。",
        ],
      },
      {
        title: "六、與第三人共用個人資料之政策",
        paragraphs: [
          "本網站絕不會提供、交換、出租或出售任何您的個人資料給其他個人、團體、私人企業或公務機關，但有法律依據或合約義務者，不在此限。",
        ],
        listIntro: "前項但書之情形包括不限於：",
        showThirdPartyExceptions: true,
      },
      {
        title: "七、Cookie 之使用",
        paragraphs: [
          "為了提供您最佳的服務，本網站會在您的電腦中放置並取用我們的 Cookie，若您不願接受 Cookie 的寫入，您可在您使用的瀏覽器功能項中設定隱私權等級為高，即可拒絕 Cookie 的寫入，但可能會導致網站某些功能無法正常執行。",
        ],
      },
      {
        title: "八、隱私權保護政策之修正",
        paragraphs: [
          "本網站隱私權保護政策將因應需求隨時進行修正，修正後的條款將刊登於網站上。",
        ],
      },
      {
        title: "九、資料之查閱與刪除",
        paragraphs: [
          "會員能隨時在網站的會員帳號查看授權的姓名與電郵資料，並且僅作為商店會員、訂單資料的內容。商店依照本人請求，可透過商店右上角聯絡我們管道提出刪除、停止使用該個人資料的要求。",
        ],
      },
    ],
    rightsRequestsTitle: "您亦可以利用「聯絡我們」提出以下要求",
    rightsRequests: [
      "刪除個人數據的權利",
      "刪除個人資料的權利",
      "您可以要求我們刪除或移除您的個人資料",
      "您可以要求刪除您的個人資料",
    ],
    sensitiveProtectionTitle: "敏感資料之保護機制",
    sensitiveProtection: [
      "加密傳輸與儲存：使用 HTTPS（SSL/TLS）加密協定傳輸所有使用者資料，並對儲存之敏感資料進行加密處理，避免未授權存取。",
      "權限控管：僅授權特定員工或委外人員可存取敏感資料，並設有帳號及權限管理制度，以防止未經授權之存取行為。",
      "存取記錄與審計：對於敏感資料之讀取與修改行為皆有留存存取記錄，以利日後稽核及追蹤。",
      "定期風險評估與漏洞修補：我們會定期檢視系統安全性、修補已知安全漏洞，並根據資安最佳實踐更新系統。",
      "資料最小化原則：僅蒐集達成特定目的所需的最少資料，並於目的完成後依規定刪除。",
    ],
    thirdPartyExceptions: [
      "經由您書面同意。",
      "法律明文規定。",
      "為免除您生命、身體、自由或財產上之危險。",
      "與公務機關或學術研究機構合作，基於公共利益為統計或學術研究而有必要，且資料經過提供者處理或蒐集者依其揭露方式無從識別特定之當事人。",
      "當您在網站的行為，違反服務條款或可能損害或妨礙網站與其他使用者權益或導致任何人遭受損害時，經網站管理單位研析揭露您的個人資料是為了辨識、聯絡或採取法律行動所必要者。",
      "有利於您的權益。",
      "本網站委託廠商協助蒐集、處理或利用您的個人資料時，將對委外廠商或個人善盡監督管理之責。",
    ],
    contactEmailLabel: "相關需求可來信至：",
    contactEmail: "spyder@spyder.com.tw",
    contactLinkPrefix: "或前往",
    contactLinkLabel: "聯絡我們",
    contactLinkSuffix: "提出需求。",
  },
  termPage: {
    metadataTitle: "服務條款",
    heroTitle: "服務條款",
    heroDescription:
      "本頁為「快客數據」服務條款全文。當您存取、瀏覽或使用本服務時，即表示您已閱讀並同意本條款。",
    introParagraphs: [
      "本「快客數據」服務條款（下稱「本條款」）規範您如何使用由 黑客數位股份有限公司（下稱「本公司」、「我們」或「本平台」）所提供之「快客數據」產品與相關服務（下稱「本服務」）。",
      "本條款構成本公司與所有使用者（包括已註冊會員及未註冊訪客，下合稱「使用者」或「您」）之間具法律約束力的協議。當您存取、瀏覽或使用本服務時，即表示您已詳細閱讀、充分理解並完全同意接受本條款所有內容及其相關法律規範的約束。如果您不同意本條款中的任何部分，請您立即停止使用本服務。",
      "本公司保留隨時調整、修改或更正本條款及相關服務內容的權利，以反映服務的變更或法律的更新。所有修改將會公布於本服務的頁面上。我們鼓勵您定期檢視本條款的最新版本。您在條款更新後繼續使用本服務，將被視為您已接受該等變更。若您不同意變更後的內容，應立即停止使用本服務。",
      "本公司有權修改本條款，相關變動將在本服務頁面進行提示。若本公司行使修改或中斷服務的權利，除本條款另有約定外，無需對使用者或第三方承擔特定責任。",
      "若本條款中任何部分被司法機關認定為無效，該無效部分並不影響本條款其他約定的效力。在無效條款修改前，相關事宜應依照中華民國《民法》、《消費者保護法》及其他相關法律規定處理。",
      "當會員（以下簡稱甲方）您在瀏覽或使用黑客數位股份有限公司（以下簡稱乙方）「快客數據」所提供的會員服務時，即表示您已經詳細審閱所有條款達三日以上，並同意接受以下所有的規範與條款。",
    ],
    sections: [
      {
        title: "服務簡介",
        paragraphs: [
          "快客數據主要提供使用者「數據串接服務」。使用者可透過免費註冊或選購付費方案，利用本服務進行數據資料的串接。",
          "本公司有權在不另行通知免費使用者的情況下，增加、變更或取消本服務中任何系統或功能的全部或一部。若使用者已為遭取消的功能支付費用，本公司將依法處理退費事宜。涉及付費會員權益的重大變更或取消，本公司於 2 個月內通知會員。",
          "您同意本公司得使用您註冊時提供的電子信箱或其他聯絡方式，向您發送服務相關的公告或商業推廣訊息。",
        ],
      },
      {
        title: "註冊與帳戶管理",
        paragraphs: [
          "使用者在快客數據完成註冊程序後，即取得「會員」資格，可使用完整的會員功能。",
          "申請註冊會員時，您承諾提供正確、完整且最新的個人資料。若資料有變動，請隨時更新。為確保服務品質，若您提供的資料不實、不完整或過期，本平台有權暫停或終止您的帳號，並拒絕您使用本服務。",
          "您有義務妥善保管您設定的會員帳號與密碼，並對透過該帳號密碼所進行的一切活動負完全責任。本公司將依據您設定的帳號和密碼來驗證您的身分。請勿將帳號出借或轉讓他人使用。若因您保管不善導致帳號遭盜用，所衍生對您、本公司或第三方的損害，應由您自行承擔。",
          "若您發現或懷疑帳號密碼被他人盜用，請立即通知本公司客服人員，以便我們及時採取相應措施。",
          "您的會員資料主要用於本公司提供服務所需。除非法律要求或主管機關命令，本公司絕不會故意或惡意將您的資料洩漏給任何無關的第三方。",
          "您有權查詢或修正您的個人會員資料，亦可請求刪除帳戶。請注意，刪除帳戶後，您將無法繼續使用依賴該帳戶資料的相關服務。",
          "您同意為您的帳號在本公司所有服務中的一切行為與活動負全部責任。",
        ],
      },
      {
        title: "數據資料處理",
        paragraphs: [
          "本公司保留基於專業判斷與市場需求，隨時優化或升級本產品功能的權利。",
          "為了提供更優質的服務（例如故障排除、系統設定或操作支援），如需要存取您在使用本產品的相關數據資料，本公司人員絕對會先經過您的同意才進行存取。如未同意，本公司人員無任何權限進行存取；亦不得對任何第三方透露、儲存。",
          "您在使用本產品所產生的任何數據，本公司皆無任何儲存行為。您的任何帳號數據，將直接應用在終端顯示介面上，無任何儲存行為。",
        ],
      },
      {
        title: "解除會員契約規範",
        paragraphs: [
          "如甲方在方案期間內，想解除使用權利，並索取退款金額，需加入快客官方 LINE 聯繫人員協助處理。退款時有以下幾點需知：",
        ],
        list: [
          "方案每月以 30 天為計算。例如方案使用剩下 5 個月又 10 天，則為剩餘 160 天（30×5 + 10）。",
          "方案退款需要額外支付 5% 處理手續費。",
          "退款後將無法使用任何快客所提供之服務。",
          "如有串接 Data Studio 一樣可享有報表權限，但所有連接器皆會失效。",
          "如付費會員兌換任何快客提供之報表可複製產品，該付費會員則一概不適用退款。",
          "快客保留最終解釋權利。",
        ],
      },
      {
        title: "會員帳號使用規範",
        paragraphs: [
          "甲方在快客數據所註冊之「會員帳號」，可用來行使快客數據之會員權利。使用者完成註冊並開始使用本服務時，即視為已完成本服務條款的審閱，並同意遵守所有條款。",
          "甲方註冊「會員帳號」時，須在快客數據「會員中心」之「註冊會員帳號」網頁上輸入您正確的個人基本資料；帳號發生問題向本公司提出申請排除時，會員須提出相關身分證明文件，本公司客服方能受理。",
          "每一個有效證件號碼限註冊一個「會員帳號」；「會員帳號」註冊成功時，即視為甲方會員帳戶開立成功。",
          "乙方於服務審核過程中，一經發現會員未提供正確資料或原提供之資料已不符合真實而未更新，將會在您提供真實資料或更新資料前，暫停會員所有提供之服務。",
          "為保障甲方的權益，「會員帳號」禁止修改。",
          "若乙方發現疑似遭盜用之「會員帳號」，將立即終止甲方「會員帳號」之使用權。註解：盜用情形在會員帳號持有者本人反映並審核排除後可立即恢復會員帳號使用。",
          "甲方負有「會員帳號」和「會員密碼」保密之義務與責任。若甲方因「會員帳號」和「會員密碼」管理不當、轉讓或借予他人使用而遭他人盜用時，其產生之一切責任均由甲方自行承擔。",
        ],
        list: [
          "避免設定過於簡單易猜的「會員帳號」和「會員密碼」或「會員密碼」與「會員帳號」設定相同。",
          "請勿將「會員帳號」和「會員密碼」透露給其他人或與其他人共用同一個「會員帳號」。",
          "若有任何人假藉活動名義要向您索取「會員帳號」、「會員密碼」、「身分證字號」時，請特別留意，勿輕信而將「會員帳號」、「會員密碼」告知對方，您可至快客數據網站上詢問專人，以免受騙。",
        ],
      },
      {
        title: "智慧財產權宣告",
        paragraphs: [
          "快客數據上之所有著作及資料以及網站畫面資料之安排，其著作權、專利權、商標權、營業秘密及其他相關智慧財產權，均為快客數據之所有人黑客數位股份有限公司（以下稱「本公司」）或授權快客數據使用之合法權利人所有，除事先經快客數據或其權利人之合法授權，任何人皆不得擅自以任何形式重製、改作、編輯、散布、傳輸、公開播送或基於租用、出售或其他目的加以使用，否則應負起因侵權行為所產生之所有法律責任，快客數據依法有權請求賠償。",
          "快客數據之內容及程式為快客數據之智慧財產權，未經快客數據授權，不得擅自重製、進行還原工程（reverse engineering）、解編（de-compile）或反向組譯（disassemble）任何功能或程式。",
          "請使用者尊重並合法地使用快客數據網站。否則，使用者即應依下述「使用者的守法義務及承諾」自負相關之責任。",
          "快客數據僅為數據資料的處理平台。若本公司收到有關內容侵權的合法通知，我們將有權刪除該使用者的帳號或相關資料，且無需事先通知。",
          "若使用者利用本平台從事任何非法活動，應自行負擔所有法律責任。本公司在必要時將採取法律途徑追訴。",
        ],
      },
      {
        title: "使用者的守法義務及承諾",
        paragraphs: [
          "您承諾絕不為任何非法目的或以任何非法方式使用快客數據網站，並承諾遵守中華民國相關法規及一切使用網際網路之國際慣例。您若係中華民國以外之使用者，並同意遵守所屬國家或地域之法令。您同意並保證不得對於使用（或未使用）快客數據之第三人、快客數據、黑客數位股份有限公司之從業人員（包括但不限於其董監事、經理人、員工、顧問、代理人等）、關係企業、廣告主以及相關合作廠商、經銷商做出侵害其權益之事。",
          "快客數據網站與黑客數位股份有限公司在使用者違法使用網站或侵害網站之權益時，得隨時拒絕或終止您使用快客數據網站的權利，該拒絕或終止無須發給任何通知。",
          "本約定書之解釋與適用，以及與本約定書有關的爭議，均應依照中華民國法律（但排除反致之適用）予以處理，並以台灣高雄地方法院為第一審管轄法院。",
          "若依法本公司需負擔法律責任時，您同意本公司所負的賠償責任上限，最高為當年度本公司就「該位使用者」使用本服務所實際獲得的收入總額。",
        ],
      },
      {
        title: "個人資料提供同意書",
        paragraphs: [
          "黑客數位股份有限公司（以下稱黑客數位）為提供更完善及多元的服務與商品，確保消費者之個人資料及隱私及消費者權益之保護，爰依個人資料保護法、相關法令規定及黑客數位隱私權政策為蒐集時之告知，以及蒐集、處理、利用您的個人資料。",
          "蒐集目的按法務部所公布電腦處理個人資料保護法之特定目的所示，在於 22 行銷、31 科技管理、37 客戶管理、53 教育或訓練行政、56 商業與技術資訊、60 統計調查與分析、65 資訊與資料庫管理、66 會員管理、97 其他合於營業登記項目或章程所定業務之需要。蒐集方式將透過加入會員或與客服聯繫方式進行個人資料之蒐集。",
          "依您所選擇的服務項目內容，您可能需要提供以下其中數項個人資料類別：",
          "黑客數位得於營運期間內使用您的資料。您提供的個人資料利用區域為全球。",
          "您所提供的個人資料，將用於進行會員驗證、會員管理、金流服務，物流送達、行銷活動、獎項兌換、數據統計及分析，個人化服務的發送（如電子報、簡訊，及透過網路媒介傳送訊息等），重要訊息或更新以及其他合於黑客數位營業登記項目或章程所定業務等。且為完成前段服務，除黑客數位處理利用外，將於必要範圍內藉由委託提供予黑客數位關係企業、提供您服務之商家以及黑客數位之協力廠商使用。",
          "於填寫個人資料時，若有得自由填寫資料的部分時，您可以自由選擇是否填寫，但您同時也可能將無法使用需要註冊或需要相關資料方得使用之服務或特定產品；此外若有發生帳號遭冒用、資料遭盜用等情形時，因您未提供或提供不實資料以致於無法確認您的身分真實性，黑客數位將無法處理您的請求。",
          "依個人資料保護法，您若就個人資料向黑客數位請求（一）查詢或請求閱覽、（二）請求製給複製本、（三）請求補充或更正、（四）請求停止蒐集、處理或利用、（五）請求刪除等情形，請您電郵至本公司信箱。如您就您的個人資料向快客數據請求不得或停止用於特定行銷或各類行銷，也請您電郵通知至前項所示信箱。",
          "本公司保留修訂本告知聲明之權利，本公司於修改後，除於官網更新內容外，也將透過官網公告，並得透過您所提供的電子郵件或其他您所存留的資訊通知您相關變更事項。",
          "快客數據對於本協議內容擁有最終解釋權和修訂權。",
        ],
        list: [
          "C001 辨識個人者，如姓名、電話、地址、電子信箱。",
          "C023 家庭其他成員之細節，如家庭狀況、法定監護人姓名。",
          "C035 休閒活動及興趣，如嗜好。",
        ],
      },
    ],
    contactEmailLabel: "相關需求可來信至：",
    contactEmail: "spyder@spyder.com.tw",
    linksPrefix: "也可前往",
    refundLinkLabel: "退款政策",
    linksAnd: "與",
    policyLinkLabel: "隱私政策",
    linksSuffix: "了解更多。",
  },
  articlesPage: {
    metadataTitle: "快客文章",
    metadataDescription: "查看連接器指標維度、數據整合與應用相關的最新文章。",
    heroTitle: "快客文章",
    heroDescription: "查看連接器指標維度、數據整合與應用相關的最新文章。",
    detailDescriptionFallback: "深入了解快客連接器指標與數據整合實務。",
  },
  notFoundPage: {
    metadataTitle: "找不到頁面",
    code: "404",
    heading: "找不到這個頁面",
    description:
      "這個網址可能已經搬家、換了名字，或是輸入時漏了幾個字。下面是大家最常找的幾個入口，直接從這裡繼續逛吧。",
    links: [
      { label: "回到首頁", description: "重新認識快客數據能幫你做什麼", href: "/" },
      { label: "方案與價格", description: "看看哪一個方案適合你的團隊", href: "/plans" },
      { label: "串接教學", description: "一步一步完成資料源串接設定", href: "/tutorial" },
      { label: "快客文章", description: "指標維度與數據應用的實戰筆記", href: "/articles" },
    ],
    contactPrefix: "還是找不到需要的內容？",
    contactLabel: "聯絡我們",
  },
  common: {
    backToArticles: "← 返回文章列表",
    articleNotFound: "找不到文章",
    noExcerpt: "尚無內容概要",
    noCover: "無封面圖",
    untitledArticle: "（未命名文章）",
    authorPrefix: "作者：",
    datePrefix: "撰寫時間：",
    tryQuickDash: "立即試用快客",
  },
};

export default tw;

type ConnectorPageDictionary = {
  metadataTitle: string;
  heroTitle: string;
  heroTitleLine2?: string;
  heroDescriptionLine1: string;
  heroDescriptionLine2: string;
  ctaLabel?: string;
  ctaHref?: string;
  supportedQueriesTitle: string;
  supportedQueries: string[];
  supportedQueriesGridClass?: string;
  supportedDestinations: {
    src: string;
    alt: string;
  }[];
  heroImage: {
    src: string;
    alt: string;
    width: number;
    height: number;
    wrapperClassName?: string;
    imageClassName?: string;
  };
  quickdashSectionTitle: string;
  quickdashSectionDescription: string;
  featureCards: {
    title: string;
    description: string;
  }[];
  stepsTitle: string;
  steps: {
    title: string;
    description: string;
    image: {
      src: string;
      alt: string;
      width: number;
      height: number;
    };
  }[];
  notesTitle?: string;
  notesDescription?: string;
  notesChecklist?: string[];
  notesImages?: {
    src: string;
    alt: string;
    caption: string;
  }[];
  notesFrameHeight?: number;
};

export interface Dictionary {
  metadata: {
    titleDefault: string;
    titleTemplate: string;
    description: string;
    keywords: string[];
    authorName: string;
    ogTitle: string;
    ogDescription: string;
    ogSiteName: string;
  };
  navbar: {
    logoText: string;
    plans: string;
    templates: string;
    tutorials: string;
    connectors: string;
    connectorMetaPage: string;
    about: string;
    articles: string;
    signUp: string;
  };
  footer: {
    sectionProduct: string;
    linkPlans: string;
    linkTutorial: string;
    sectionCompany: string;
    linkAbout: string;
    linkArticles: string;
    linkContact: string;
    sectionSupport: string;
    linkRefund: string;
    linkTerms: string;
    linkPrivacy: string;
    logoText: string;
    tagline: string;
    facebookPage: string;
    taxId: string;
    copyright: string;
  };
  hero: {
    headline: string;
    headlineAccent: string;
    subHeadlines: (string | number)[];
    ctaSignUp: string;
    platformsLabel: string;
    tabAll: string;
    dashOverview: string;
    dashRange: string;
    kpiImpressions: string;
    kpiClicks: string;
    kpiConversions: string;
    kpiConvValue: string;
    trendTitle: string;
    seriesImpressions: string;
    seriesClicks: string;
    pieTitle: string;
    tableTitle: string;
    tableCols: string[];
    chip: string;
    otherLabel: string;
  };
  pricing: {
    freeTrial: string;
    days: string;
    mostPopular: string;
    aiMcpBadgeLabel: string;
    perYear: string;
    perMonth: string;
    billingMonthly: string;
    billingYearly: string;
    yearlyDiscountBadge: string;
    needHelp: string;
    contactLine: string;
    contactMessenger: string;
  };
  pricingPlans: {
    currency: string;
    name: string;
    priceYearly: number;
    description: string;
    isRecommended: boolean;
    showAiMcpBadge?: boolean;
    ctaText: string;
    features: {
      feature: string;
      included: boolean;
    }[];
  }[];
  process: {
    heading: string;
    headingUnit: string;
    unitSingular: string;
    step1Title: string;
    step2Title: string;
    step3Title: string;
    step4Title: string;
  };
  featureCards: {
    heading: string;
    headingUnit: string;
    metaTitle: string;
    metaDesc: string;
    googleTitle: string;
    lineTitle: string;
    ecommerceTitle: string;
    ecommerceDesc: string;
  };
  photoCarousel: {
    heading: string;
    dimensions_total: string;
    dimensions_brand: string;
    dimensions_ads: string;
    graphTitle: string;
  };
  featureShowcase: {
    heading: string;
    subheading: string;
    headingUnit: string;
    cards: {
      title: string;
      description: string;
    }[];
  };
  mcpShowcase: McpShowcaseDictionary;
  video: {
    heading: string;
    subHeading: string;
    defaultTitle: string;
  };
  plansPage: {
    metadataTitle: string;
    heroTitleLine1: string;
    heroTitleLine2: string;
    heroSubtitle: string;
    additionalNotesTitle: string;
    additionalNotes: string[];
    sampleReportTitle: string;
  };
  templatesPage: {
    metadataTitle: string;
    heroTitle: string;
    heroDescription: string;
    ctaLabel: string;
    ctaHref: string;
    cards: {
      category: string;
      typeLabel: string;
      title: string;
      description: string;
      image: string;
      href: string;
      icon?: string;
      aspectRatio?: string;
      imageWidth?: number;
      imageHeight?: number;
    }[];
  };
  mcpPage: {
    metadataTitle: string;
    hero: {
      title: string;
      ctaUrl: string;
      ctaText: string;
      youtubeId: string;
      youtubeTitle: string;
    };
    aiTools: {
      title: string;
      description: string;
      tools: {
        name: string;
        description: string;
        icon?: string;
      }[];
    };
    dataSources: {
      title: string;
      description: string;
      sources: {
        name: string;
        icon: string;
      }[];
    };
    tools: McpDictionary["tools"];
  };
  tutorialPage: {
    metadataTitle: string;
    quickStart: {
      title: string;
      description: string;
      ctaUrl: string;
      ctaText: string;
      youtubeId: string;
      youtubeTitle: string;
    };
    steps: {
      title: string;
      description: string;
      emphasis?: string;
      image?: {
        src: string;
        alt: string;
      };
      height?: number;
      width?: number;
      actionHref?: string;
      actionLabel?: string;
    }[];
    extraSections: {
      title: string;
      description: string;
      emphasis?: string;
      image?: {
        src: string;
        alt: string;
      };
      height?: number;
      width?: number;
    }[];
  };
  gsheetTutorialPage: {
    metadataTitle: string;
    quickStart: {
      title: string;
      description: string;
      ctaUrl: string;
      ctaText: string;
      youtubeId: string;
      youtubeTitle: string;
    };
    steps: {
      title: string;
      description: string;
      emphasis?: string;
      image?: {
        src: string;
        alt: string;
      };
      height?: number;
      width?: number;
      actionHref?: string;
      actionLabel?: string;
    }[];
    extraSections: {
      title: string;
      description: string;
      emphasis?: string;
      image?: {
        src: string;
        alt: string;
      };
      height?: number;
      width?: number;
    }[];
  };
  extensionTutorialPage: {
    metadataTitle: string;
    quickStart: {
      title: string;
      description: string;
      ctaUrl: string;
      ctaText: string;
      youtubeId: string;
      youtubeTitle: string;
    };
    toolsSection: {
      title: string;
      description: string;
      tools: {
        name: string;
        description: string;
      }[];
    };
    steps: {
      title: string;
      description: string;
      emphasis?: string;
      image?: {
        src: string;
        alt: string;
      };
      height?: number;
      width?: number;
      actionHref?: string;
      actionLabel?: string;
    }[];
    extraSections: {
      title: string;
      description: string;
      emphasis?: string;
      image?: {
        src: string;
        alt: string;
      };
      height?: number;
      width?: number;
    }[];
  };
  metaAdsPage: {
    metadataTitle: string;
    heroTitle: string;
    heroDescriptionLine1: string;
    heroDescriptionLine2: string;
    ctaLabel: string;
    supportedQueriesTitle: string;
    supportedQueries: string[];
    supportedDestinations: {
      src: string;
      alt: string;
    }[];
    quickdashSectionTitle: string;
    quickdashSectionDescription: string;
    featureCards: {
      title: string;
      description: string;
    }[];
    stepsTitle: string;
    steps: {
      title: string;
      description: string;
      image: {
        src: string;
        alt: string;
        width: number;
        height: number;
      };
    }[];
    notesTitle: string;
    notesDescription: string;
    notesImages: {
      src: string;
      alt: string;
      caption: string;
    }[];
  };
  igInsightsPage: {
    metadataTitle: string;
    heroTitle: string;
    heroDescriptionLine1: string;
    heroDescriptionLine2: string;
    ctaLabel: string;
    supportedQueriesTitle: string;
    supportedQueries: string[];
    supportedDestinations: {
      src: string;
      alt: string;
    }[];
    quickdashSectionTitle: string;
    quickdashSectionDescription: string;
    featureCards: {
      title: string;
      description: string;
    }[];
    stepsTitle: string;
    steps: {
      title: string;
      description: string;
      image: {
        src: string;
        alt: string;
        width: number;
        height: number;
      };
    }[];
    notesTitle: string;
    notesDescription: string;
    notesChecklist: string[];
    notesImages: {
      src: string;
      alt: string;
      caption: string;
    }[];
  };
  threadsPage: ConnectorPageDictionary;
  lapPage: ConnectorPageDictionary;
  metaPagePage: ConnectorPageDictionary;
  gaPage: ConnectorPageDictionary;
  gadsPage: ConnectorPageDictionary;
  bingPage: ConnectorPageDictionary;
  gbpPage: ConnectorPageDictionary;
  openaiPage: ConnectorPageDictionary;
  gscPage: ConnectorPageDictionary;
  cyberbizPage: ConnectorPageDictionary;
  shopifyPage: ConnectorPageDictionary;
  shoplinePage: ConnectorPageDictionary;
  oneshopPage: ConnectorPageDictionary;
  rutenPage: ConnectorPageDictionary;
  aboutPage: {
    metadataTitle: string;
    heroTitleLine1: string;
    heroTitleHighlight: string;
    heroDescription: string;
    stats: {
      value: string;
      label: string;
    }[];
    featuresTitle: string;
    featuresSubtitle: string;
    productFeatures: {
      icon: string;
      title: string;
      description: string;
    }[];
    platformsTitle: string;
    platformsSubtitle: string;
    platforms: {
      name: string;
      iconSrc: string;
    }[];
    ctoBadge: string;
    ctoTitle: string;
    ctoParagraphs: string[];
    ctoSocialLinks: {
      label: string;
      href: string;
      iconSrc: string;
    }[];
    awardsSection: {
      sectionLabel: string;
      items: {
        growth: {
          title: string;
          subtitle: string;
          description: string;
        };
        strategy: {
          title: string;
          subtitle: string;
          description: string;
        };
      };
      highlight: {
        title: string;
        subtitle: string;
        description: string;
      };
      ariaPrevious: string;
      ariaNext: string;
      ariaSwitchTo: string;
    };
    testimonialsTitle: string;
    testimonials: {
      name: string;
      company: string;
      avatar: string;
      role: string;
    }[];
    hackerSectionCtaText: string;
    hackerSectionCtaHref: string;
    hackerSectionTitle: string;
    hackerSectionParagraphs: string[];
    hackerSectionServices: string[];
    hackerSectionStats: {
      value: string;
      label: string;
    }[];
    hackerSectionAwardText: string;
  };
  contactPage: {
    metadataTitle: string;
    heroTitle: string;
    heroDescription: string;
    lineButtonLabel: string;
    lineOfficialUrl: string;
  };
  refundPage: {
    metadataTitle: string;
    heroTitle: string;
    heroDescription: string;
    lineButtonLabel: string;
    lineOfficialUrl: string;
    stepsTitle: string;
    steps: {
      title: string;
      description: string;
      items?: string[];
    }[];
    notesTitle: string;
    notes: string[];
  };
  policyPage: {
    metadataTitle: string;
    heroTitle: string;
    heroDescription: string;
    sections: {
      title: string;
      paragraphs: string[];
      listIntro?: string;
      subsections?: {
        heading: string;
        paragraphs?: string[];
        items?: string[];
        notes?: string[];
      }[];
      showRightsRequests?: boolean;
      showThirdPartyExceptions?: boolean;
    }[];
    rightsRequestsTitle: string;
    rightsRequests: string[];
    sensitiveProtectionTitle: string;
    sensitiveProtection: string[];
    thirdPartyExceptions: string[];
    contactEmailLabel: string;
    contactEmail: string;
    contactLinkPrefix: string;
    contactLinkLabel: string;
    contactLinkSuffix: string;
  };
  termPage: {
    metadataTitle: string;
    heroTitle: string;
    heroDescription: string;
    introParagraphs: string[];
    sections: {
      title: string;
      paragraphs: string[];
      list?: string[];
    }[];
    contactEmailLabel: string;
    contactEmail: string;
    linksPrefix: string;
    refundLinkLabel: string;
    linksAnd: string;
    policyLinkLabel: string;
    linksSuffix: string;
  };
  articlesPage: {
    metadataTitle: string;
    metadataDescription: string;
    heroTitle: string;
    heroDescription: string;
    detailDescriptionFallback: string;
  };
  notFoundPage: {
    metadataTitle: string;
    code: string;
    heading: string;
    description: string;
    links: {
      label: string;
      description: string;
      href: string;
    }[];
    contactPrefix: string;
    contactLabel: string;
  };
  common: {
    backToArticles: string;
    articleNotFound: string;
    noExcerpt: string;
    noCover: string;
    untitledArticle: string;
    authorPrefix: string;
    datePrefix: string;
    tryQuickDash: string;
  };
}
